import { useTranslations } from 'next-intl';
import { socialProfiles } from 'config/global';

// "Where we build" — Manly, with an animated sunrise-over-the-sea scene.
// Card background per theme: sunrise-tinted white / the original navy blend.
const PLACECARD_BG = [
  'bg-white bg-[image:radial-gradient(120%_150%_at_14%_16%,rgba(247,203,45,0.16)_0%,rgba(247,203,45,0.03)_32%,transparent_52%),radial-gradient(120%_140%_at_92%_90%,rgba(189,222,255,0.5)_0%,transparent_56%)]',
  'marketing-dark:bg-[image:radial-gradient(120%_150%_at_14%_16%,rgba(247,203,45,0.18)_0%,rgba(247,203,45,0.04)_32%,transparent_52%),radial-gradient(120%_140%_at_92%_90%,rgba(45,86,210,0.4)_0%,transparent_56%),linear-gradient(160deg,#0e1f52_0%,#0a1838_72%)]',
].join(' ');

const Coast = ({ label }: { label: string }) => (
  <svg
    viewBox="0 0 300 300"
    role="img"
    aria-label={label}
    className={styles.coastSvg}
  >
    <defs>
      <clipPath id="coastClip">
        <circle cx="150" cy="150" r="132" />
      </clipPath>
      <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#0b1f4d" />
        <stop offset=".5" stopColor="#244a93" />
        <stop offset=".82" stopColor="#6f86c9" />
        <stop offset="1" stopColor="#f0c64a" />
      </linearGradient>
      <radialGradient id="sung" cx="50%" cy="50%" r="50%">
        <stop offset="0" stopColor="#FDE47A" />
        <stop offset=".55" stopColor="#F7CB2D" />
        <stop offset="1" stopColor="#F7CB2D" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="sea" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#cfa94a" />
        <stop offset=".18" stopColor="#4f6fb0" />
        <stop offset="1" stopColor="#16306a" />
      </linearGradient>
    </defs>
    <g clipPath="url(#coastClip)">
      <rect x="18" y="18" width="264" height="264" fill="url(#sky)" />
      <circle
        cx="150"
        cy="176"
        r="46"
        fill="url(#sung)"
        className={styles.sun}
      />
      <circle cx="150" cy="176" r="27" fill="#FDE47A" />
      <rect x="18" y="182" width="264" height="100" fill="url(#sea)" />
      <g fill="#FDE47A" opacity=".72">
        <rect x="138" y="196" width="24" height="4" rx="2" />
        <rect x="126" y="208" width="48" height="4" rx="2" opacity=".75" />
        <rect x="132" y="220" width="36" height="4" rx="2" opacity=".5" />
      </g>
      <g
        fill="none"
        stroke="#dbe6ff"
        strokeWidth="3"
        strokeLinecap="round"
        opacity=".5"
      >
        <path
          className={styles.wave1}
          d="M40 236c14 0 14 8 28 8s14-8 28-8 14 8 28 8 14-8 28-8 14 8 28 8 14-8 28-8"
        />
        <path
          className={styles.wave2}
          d="M30 256c16 0 16 8 32 8s16-8 32-8 16 8 32 8 16-8 32-8 16 8 32 8 16-8 32-8"
          opacity=".7"
        />
      </g>
    </g>
    <circle
      cx="150"
      cy="150"
      r="132"
      fill="none"
      stroke="rgba(255,255,255,.18)"
      strokeWidth="1.5"
    />
  </svg>
);

export const Section25 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.head}>
          <div className={styles.eyebrow}>{t('pages.about.place.eyebrow')}</div>
        </div>

        <div className={`${styles.card} ${PLACECARD_BG}`}>
          <div className={styles.pt}>
            <span className={styles.pin}>
              <svg viewBox="0 0 24 24" className={styles.pinIcon}>
                <path d="M12 21s-7-5.6-7-11a7 7 0 0 1 14 0c0 5.4-7 11-7 11z" />
                <circle cx="12" cy="10" r="2.4" />
              </svg>
              {t('pages.about.place.pin')}
            </span>
            <h2 className={styles.title}>
              {t.rich('pages.about.place.title', {
                highlight: (chunks) => (
                  <span className={styles.highlight}>{chunks}</span>
                ),
              })}
            </h2>
            <p className={styles.body}>{t('pages.about.place.body1')}</p>
            <p className={styles.body}>{t('pages.about.place.body2')}</p>
            <p className={styles.body}>
              <a
                href={socialProfiles.linkedIn}
                className={styles.link}
                target="_blank"
                rel="noreferrer noopener"
              >
                {t('pages.about.place.linkedin')}
              </a>
            </p>
          </div>

          <div className={styles.mascotwrap}>
            <span className={styles.mglow} aria-hidden="true" />
            <div className={styles.coast}>
              <Coast label={t('pages.about.place.coastAlt')} />
            </div>
            <div className={styles.chips} aria-hidden="true">
              <div className={styles.chip} style={{ top: '8px', right: 0 }}>
                <span
                  className={styles.chipIcon}
                  style={{ background: 'rgba(247,203,45,0.18)' }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className={`${styles.chipGlyph} stroke-[#B18E1E] marketing-dark:stroke-[#FDE47A]`}
                  >
                    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4" />
                    <circle cx="12" cy="12" r="4" />
                  </svg>
                </span>
                <span>
                  {t('pages.about.place.chipSunrise')}
                  <small className={styles.chipSub}>AEST · GMT+10</small>
                </span>
              </div>
              <div
                className={styles.chip}
                style={{ bottom: '24px', left: '-6px' }}
              >
                <span
                  className={styles.chipIcon}
                  style={{ background: 'rgba(127,168,255,0.2)' }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className={`${styles.chipGlyph} stroke-mf-blue marketing-dark:stroke-[#9cc0ff]`}
                  >
                    <path d="M2 16c2 0 2-1.6 4-1.6S8 16 10 16s2-1.6 4-1.6S16 16 18 16s2-1.6 4-1.6M2 20c2 0 2-1.6 4-1.6S8 20 10 20s2-1.6 4-1.6S16 20 18 20s2-1.6 4-1.6" />
                    <path d="M8 11l4-7 4 7" />
                  </svg>
                </span>
                <span>{t('pages.about.place.chipBeaches')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy pt-10 pb-2.5',
  wrap: 'mx-auto max-w-[1200px] px-6 lg:px-10',
  head: 'mb-[34px] text-center',
  eyebrow:
    'text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  card: [
    'relative grid grid-cols-1 items-center gap-12 overflow-hidden rounded-[28px] border px-8 py-12 text-center lg:grid-cols-[1.04fr_0.96fr] lg:px-[60px] lg:py-[58px] lg:text-left',
    'border-[#F0F0F0] marketing-dark:border-white/10',
  ].join(' '),
  pt: 'relative z-[2]',
  pin: [
    'mx-auto mb-[22px] inline-flex items-center gap-[9px] rounded-full border px-4 py-[9px] text-[13.5px] font-semibold lg:mx-0',
    'border-[#DADADA] bg-white text-mf-off-black-600',
    'marketing-dark:border-white/[0.16] marketing-dark:bg-white/[0.07] marketing-dark:text-white',
  ].join(' '),
  pinIcon: 'h-4 w-4 fill-none stroke-mf-yellow stroke-2',
  title:
    'mx-auto max-w-[440px] font-inter text-[32px] font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[38px] lg:mx-0',
  // Brand yellow reads on navy; the darker gold keeps contrast on white.
  highlight: 'text-[#B18E1E] marketing-dark:text-mf-yellow',
  body: 'mx-auto mt-[18px] max-w-[470px] text-[17.5px] leading-[1.66] text-mf-gray-600 marketing-dark:text-white/70 lg:mx-0',
  link: [
    'font-semibold underline underline-offset-4 transition-colors',
    'text-mf-off-black-600 decoration-mf-gray-300 hover:text-mf-blue',
    'marketing-dark:text-white marketing-dark:decoration-white/40 marketing-dark:hover:text-mf-coral',
  ].join(' '),
  mascotwrap: 'relative z-[2] flex min-h-[300px] items-center justify-center',
  mglow: [
    'absolute h-[340px] w-[340px] rounded-full',
    '[background:radial-gradient(circle,rgba(189,222,255,0.55),transparent_64%)]',
    'marketing-dark:[background:radial-gradient(circle,rgba(127,168,255,0.28),transparent_64%)]',
  ].join(' '),
  coast: [
    'relative z-[2] h-[240px] w-[240px] sm:h-[300px] sm:w-[300px]',
    '[filter:drop-shadow(0_18px_36px_rgba(20,30,60,0.2))]',
    'marketing-dark:[filter:drop-shadow(0_24px_50px_rgba(2,8,30,0.5))]',
  ].join(' '),
  coastSvg: 'block h-full w-full',
  sun: 'motion-safe:animate-coast-sun [transform-box:fill-box] [transform-origin:center]',
  wave1: 'motion-safe:animate-coast-wave',
  wave2:
    'motion-safe:animate-coast-wave [animation-duration:9s] [animation-direction:reverse]',
  chips: 'pointer-events-none absolute inset-0 z-[4] hidden sm:block',
  chip: [
    'absolute flex items-center gap-[9px] whitespace-nowrap rounded-xl border px-[13px] py-[9px] text-[12.5px] font-semibold motion-safe:animate-floaty',
    'border-[#DEDEDE] bg-white text-mf-off-black-600 shadow-[0_16px_36px_rgba(20,30,60,0.16)]',
    'marketing-dark:border-white/[0.13] marketing-dark:bg-[#0b1430] marketing-dark:text-white',
    'marketing-dark:shadow-[0_16px_36px_rgba(0,0,0,0.45)]',
  ].join(' '),
  chipIcon:
    'flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-[7px]',
  chipGlyph: 'h-[15px] w-[15px] fill-none stroke-2',
  chipSub:
    'mt-px block text-[10px] font-normal text-mf-gray-500 marketing-dark:text-white/50',
};
